// src/components/SearchableSelect.jsx
import { useEffect, useMemo, useRef, useState } from "react";

export default function SearchableSelect({
  options = [],
  value,
  onChange,
  includeAll = false,
  allValue = "all",
  allLabel = "All",
  placeholder = "Select…",
  loading = false,
  error = "",
  menuWidthClass = "w-72",
}) {
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState("");
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  const normalized = useMemo(() => {
    const base = Array.isArray(options) ? options : [];
    const list = includeAll ? [allLabel, ...base] : base;
    const f = filter.trim().toLowerCase();
    return f ? list.filter((v) => String(v).toLowerCase().includes(f)) : list;
  }, [options, filter, includeAll, allLabel]);

  const display = value
    ? value === allValue
      ? allLabel
      : value
    : includeAll && value === "" // for empty-as-all cases
    ? allLabel
    : placeholder;

  const handleSelect = (v) => {
    const out = includeAll && (v === allLabel || v === allValue) ? allValue : v;
    onChange?.(out);
    setOpen(false);
  };

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center justify-between rounded-lg border border-sky-900/30 bg-[#0a1224]/80 backdrop-blur-sm px-3 h-9 text-sm text-white shadow-[0_0_10px_rgba(56,189,248,0.08)] hover:bg-[#0a1224]/90"
      >
        <span className="truncate max-w-[14rem]">
          {display || placeholder}
        </span>
        <svg className="ml-2 h-4 w-4 opacity-70" viewBox="0 0 20 20" fill="currentColor">
          <path d="M5.25 7.5L10 12.25L14.75 7.5H5.25Z" />
        </svg>
      </button>

      {open && (
        <div
          className={`absolute mt-2 left-0 ${menuWidthClass} z-[200] overflow-hidden rounded-lg border border-sky-900/30 bg-[#0a1224]/95 backdrop-blur-md shadow-[0_10px_30px_rgba(0,0,0,0.35)]`}
        >
          <div className="flex items-center gap-2 px-3 py-2 border-b border-white/10">
            <svg className="h-4 w-4 text-sky-300/80" viewBox="0 0 20 20" fill="currentColor">
              <path d="M12.9 14.32a8 8 0 111.414-1.414l3.387 3.387-1.414 1.414-3.387-3.387zM8 14A6 6 0 108 2a6 6 0 000 12z" />
            </svg>
            <input
              autoFocus
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Type to filter…"
              className="flex-1 bg-transparent outline-none text-sm text-white placeholder:text-neutral-500"
            />
          </div>

          {error && (
            <div className="px-3 py-2 text-xs text-red-300">{error}</div>
          )}
          {loading && !error && (
            <div className="px-3 py-2 text-xs text-neutral-300">Loading…</div>
          )}

          {!loading && !error && (
            <div className="max-h-64 overflow-auto">
              {normalized.length === 0 ? (
                <div className="px-3 py-2 text-sm text-neutral-400">No results</div>
              ) : (
                normalized.map((opt) => {
                  const label = includeAll && opt === allLabel ? allLabel : opt;
                  const selected =
                    (includeAll && value === allValue && label === allLabel) ||
                    value === label;
                  return (
                    <button
                      key={label}
                      type="button"
                      onClick={() => handleSelect(label)}
                      className={`block w-full text-left px-3 py-2 text-sm hover:bg-white/5 ${
                        selected ? "text-sky-300" : "text-neutral-200"
                      }`}
                      title={label}
                    >
                      {label}
                    </button>
                  );
                })
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
